import { Dropdown } from "antd";
import classNames from "classnames";
import log from "cslog";
import { useAtom, useSetAtom } from "jotai";
import { startCase } from "lodash";
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import { getHeroIcon } from "../_helpers/heroicons";
import { getPublicImageURL } from "../_helpers/image";
import ImagePicker from "../editor/imagepicker/ImagePicker";
import { canvasAtom, currentActionAtom } from "./atoms";
import ChatSuggestions from "./ChatSuggestions";
import { useSocket } from "./socket/SocketManager";
import styles from "./styles/ChatInputBox.module.css";

const ChatInputBox = forwardRef(
    (
        {
            initialValue = "",
            show_reset_button = false,
            disabled = false,
            show_suggestions = true,
            wrapper_style = {},
            is_bottom = false,
            onSubmit: externalOnSubmit = null,
            agent = "master",
            setAgent = () => {},
        },
        ref,
    ) => {
        const { sendMessage, isLoading, reset } = useSocket();
        const [value, onChange] = useState("");
        const [images, setImages] = useState([]);
        const setShowCanvas = useSetAtom(canvasAtom);
        const [current_action, setCurrentAction] = useAtom(currentActionAtom);
        const inputRef = useRef(null);

        // Expose methods to parent components through ref
        useImperativeHandle(ref, () => ({
            setInputText: (text) => {
                onChange(text);
                // Focus the textarea after setting text
                inputRef.current?.focus();
            },
            getValue: () => value,
        }));

        useEffect(() => {
            if (current_action) {
                switch (current_action.id) {
                    case "new_chat":
                        reset();
                        setCurrentAction(null);
                        break;
                    default:
                        break;
                }
            }
        }, [current_action]);

        useEffect(() => {
            onChange(initialValue);
        }, [initialValue]);

        const onSubmit = () => {
            if (value === "") return;

            if (externalOnSubmit) {
                externalOnSubmit(value);
            } else {
                sendMessage(value, images, agent);
            }

            onChange("");
            setImages([]);
        };

        const onImageSubmit = (selectedImages) => {
            const formattedImages = selectedImages.map((image) => ({
                ...image,
                type: "image",
                params: {
                    srcType: "lib",
                    ...image,
                },
                value: getPublicImageURL(image.id),
            }));
            setImages((old) => [...old, ...formattedImages]);
        };

        useEffect(() => {
            inputRef.current?.focus();
        }, []);

        return (
            <div className={styles.wrapper} style={wrapper_style}>
                {show_suggestions && (
                    <ChatSuggestions
                        onReset={reset}
                        onSendMessage={(msg) => {
                            sendMessage(msg);
                        }}
                        is_bottom={is_bottom}
                    />
                )}
                <div
                    className={classNames({
                        [styles.box]: true,
                        [styles.box_bottom]: is_bottom,
                    })}
                    style={{
                        backgroundColor: disabled ? "#eee" : "white",
                    }}
                >
                    {images.length > 0 && (
                        <ImageListPreview
                            images={images}
                            setImages={setImages}
                        />
                    )}

                    <textarea
                        ref={inputRef}
                        className={styles.text_area}
                        type="text"
                        placeholder="Type your message..."
                        value={value}
                        onChange={(e) => {
                            onChange(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                onSubmit();
                            }
                        }}
                        disabled={disabled}
                    />

                    <div className={styles.bottom_panel}>
                        <div className={styles.bottom_panel_left}>
                            <Dropdown
                                trigger="click"
                                menu={{
                                    items: [
                                        {
                                            key: "select",
                                            label: (
                                                <ImagePicker
                                                    trigger={
                                                        <div>Select Image</div>
                                                    }
                                                    many={false}
                                                    onSelect={(images) => {
                                                        log.d(
                                                            images,
                                                            "Images from Image Picker",
                                                        );
                                                        onImageSubmit(images);
                                                    }}
                                                />
                                            ),
                                            icon: (
                                                <i className="fas fa-image"></i>
                                            ),
                                        },
                                        {
                                            key: "upload",
                                            label: (
                                                <div
                                                    onClick={() => {
                                                        alert(
                                                            "Use 'select image' for now and later implement this",
                                                        );
                                                    }}
                                                >
                                                    Upload an Image
                                                </div>
                                            ),
                                            icon: (
                                                <i className="fas fa-upload"></i>
                                            ),
                                        },
                                    ],
                                }}
                            >
                                <div
                                    className={styles.action_chip}
                                    style={{ padding: "0 8px" }}
                                >
                                    {getHeroIcon("plus", { width: "18px" })}
                                </div>
                            </Dropdown>
                            <AgentPicker value={agent} onChange={setAgent} />
                        </div>

                        <div className={styles.bottom_panel_left}>
                            {show_reset_button && (
                                <button
                                    className={styles.reset_button}
                                    onClick={reset}
                                >
                                    <i className="fas fa-plus"></i>
                                    New Chat
                                </button>
                            )}

                            <button
                                className={styles.send_button}
                                // disabled={isLoading || !isConnected}
                                // style={isConnected ? {} : { backgroundColor: "#ffc4c4" }}
                                onClick={onSubmit}
                            >
                                <i className="fas fa-arrow-up"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

export default ChatInputBox;

function ImageListPreview({ images, setImages }) {
    return (
        <div className={styles.images_list}>
            {images.map((image, index) => {
                return (
                    <ImageItem
                        key={index}
                        image={image}
                        onRemove={() => {
                            setImages((old) => {
                                old.splice(index, 1);
                                return [...old];
                            });
                        }}
                    />
                );
            })}
        </div>
    );
}

function ImageItem({ image, onRemove }) {
    const ImageSrc =
        image.url ||
        image.src?.medium ||
        image.previewURL ||
        image.urls?.small ||
        image.media?.[0]?.gif?.url ||
        image.src;

    return (
        <div className={styles.image_item}>
            <img className={styles.image} src={ImageSrc} alt="image" />
            <i
                className="fas fa-times"
                onClick={() => {
                    onRemove();
                }}
            ></i>
        </div>
    );
}

function AgentPicker({ value, onChange }) {
    const items = [
        {
            key: "master",
            label: "Master",
            onClick: () => onChange("master"),
        },
        {
            key: "graphics_designer",
            label: "Graphics Designer",
            onClick: () => onChange("graphics_designer"),
        },
        {
            key: "blogger",
            label: "Blogger",
            onClick: () => onChange("blogger"),
        },
    ];
    return (
        <Dropdown
            menu={{
                items,
                selectable: true,
                defaultSelectedKeys: ["3"],
            }}
        >
            <div className={styles.action_chip}>
                {value ? startCase(value) : "Select Agent"}
                {getHeroIcon("chevron-down", { width: "18px" })}
            </div>
        </Dropdown>
    );
}
